import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Check, Settings } from 'lucide-react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('dodipa_cookie_consent');
    if (!cookieConsent) {
      // Show banner after a small delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const acceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('dodipa_cookie_consent', JSON.stringify(consent));
    setShowBanner(false);
    // Ricarica per attivare Google Analytics
    window.location.reload();
  };

  const acceptNecessary = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('dodipa_cookie_consent', JSON.stringify(consent));
    setShowBanner(false);
  };

  const savePreferences = () => {
    const consent = {
      ...preferences,
      necessary: true, // Always true
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('dodipa_cookie_consent', JSON.stringify(consent));
    setShowBanner(false);
    setShowPreferences(false);
    // Ricarica se analytics è stato attivato
    if (preferences.analytics) {
      window.location.reload();
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="cookie-banner">
      <div className="w-full max-w-4xl bg-[#0B1121] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
        {!showPreferences ? (
          // Main Banner
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#F5A547]/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-[#F5A547]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-heading text-xl font-semibold mb-2">
                  Informativa sui Cookie
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  Utilizziamo i cookie per migliorare la tua esperienza sul nostro sito. 
                  I cookie tecnici sono necessari per il funzionamento del sito, mentre quelli 
                  analitici e di marketing ci aiutano a migliorare i nostri servizi.
                  Per maggiori informazioni, consulta la nostra{' '}
                  <Link to="/cookie-policy" className="text-[#5CBDB0] hover:underline">
                    Cookie Policy
                  </Link>{' '}
                  e l'
                  <Link to="/privacy-policy" className="text-[#5CBDB0] hover:underline">
                    Informativa Privacy
                  </Link>.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={acceptAll}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-[#5CBDB0] text-black font-semibold rounded-full hover:bg-[#4da99d] transition-colors"
                    data-testid="cookie-accept-all"
                  >
                    <Check className="w-4 h-4" />
                    Accetta tutti
                  </button>
                  <button
                    onClick={acceptNecessary}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-colors"
                    data-testid="cookie-accept-necessary"
                  >
                    Solo necessari
                  </button>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="flex items-center justify-center gap-2 px-6 py-3 border border-white/20 text-white/70 font-semibold rounded-full hover:bg-white/5 transition-colors"
                    data-testid="cookie-preferences"
                  >
                    <Settings className="w-4 h-4" />
                    Personalizza
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Preferences Panel
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-heading text-xl font-semibold">
                Preferenze Cookie
              </h3>
              <button 
                onClick={() => setShowPreferences(false)}
                className="text-white/50 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Cookie Tecnici (Necessari)</div>
                    <div className="text-white/50 text-sm mt-1">
                      Essenziali per il funzionamento del sito. Non possono essere disattivati.
                    </div>
                  </div>
                  <div className="w-12 h-6 bg-[#5CBDB0] rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Cookie Analitici</div>
                    <div className="text-white/50 text-sm mt-1">
                      Ci aiutano a capire come i visitatori interagiscono con il sito.
                    </div>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.analytics ? 'bg-[#5CBDB0] justify-end' : 'bg-white/20 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Cookie di Marketing</div>
                    <div className="text-white/50 text-sm mt-1">
                      Utilizzati per mostrare annunci pertinenti ai tuoi interessi.
                    </div>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.marketing ? 'bg-[#5CBDB0] justify-end' : 'bg-white/20 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={savePreferences}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#5CBDB0] text-black font-semibold rounded-full hover:bg-[#4da99d] transition-colors"
                data-testid="cookie-save-preferences"
              >
                <Check className="w-4 h-4" />
                Salva preferenze
              </button>
              <button
                onClick={acceptAll}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-colors"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        )}

        {/* GDPR Compliance Note */}
        <div className="px-6 md:px-8 py-3 bg-white/5 border-t border-white/10">
          <p className="text-white/40 text-xs text-center">
            Conforme al Regolamento UE 2016/679 (GDPR) e alla normativa italiana sulla privacy.
            Puoi modificare le tue preferenze in qualsiasi momento.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
